import React, { useEffect, useState } from 'react';
import { db } from '../firebase/firebaseConfig';
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  deleteDoc,
} from 'firebase/firestore';
import Fuse from 'fuse.js';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
// AI Semantic Matching Function (calls backend to compute similarity score)

const getSemanticScore = async (claimText, lostText) => {
  try {
    const response = await fetch('http://localhost:5000/api/semantic-match', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        claimText,
        lostItems: [{ id: 'lost1', text: lostText }]
      })
    });

    const data = await response.json();
    return data?.bestMatch?.score || 0;
  } catch (err) {
    console.error('AI match error:', err);
    return 0;
  }
};

const ItemMatcher = () => {
  const [matches, setMatches] = useState([]);

 // Fetch lost and claim items and run matching logic
  useEffect(() => {
    const fetchAndMatch = async () => {
      try {
        const lostSnap = await getDocs(collection(db, 'lostItems'));
        const claimSnap = await getDocs(collection(db, 'claims'));

        const lostItems = lostSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const claims = claimSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        const fuse = new Fuse(lostItems, {
          keys: ['itemName', 'description'],
          includeScore: true,
          threshold: 0.4,
        });

        const results = [];

        for (const claim of claims) {
          if (claim.matched) continue;

          let bestMatch = null;
          let scoreValue = 1;
          let scoreLabel = '1 (Exact Match)';

          
          bestMatch = lostItems.find(
            (lost) =>
              lost.itemName?.toLowerCase() === claim.itemName?.toLowerCase() &&
              lost.description?.toLowerCase() === claim.itemDescription?.toLowerCase()
          );

          if (!bestMatch) {
            const query = `${claim.itemName} ${claim.itemDescription || ''}`;
            const fuseResult = fuse.search(query);
            if (fuseResult.length > 0) {
              bestMatch = fuseResult[0].item;

             // Using AI similarity API to measure the similarity score 
              const claimedText = `${claim.itemName} ${claim.itemDescription || ''}`;
              const lostText = `${bestMatch.itemName} ${bestMatch.description || ''}`;
              const aiScore = await getSemanticScore(claimedText, lostText);

              scoreValue = aiScore;
              scoreLabel = `AI Match Score: ${aiScore.toFixed(2)}`;
            } else {
              continue;
            }
          }

          results.push({
            claimId: claim.id,
            claimItem: claim,
            matchedLostItem: bestMatch,
            score: scoreLabel,
            rawScore: scoreValue,
          });

          await updateDoc(doc(db, 'claims', claim.id), {
            matched: true,
            matchedItemId: bestMatch.id,
          });
        }

        setMatches(results);
      } catch (error) {
        console.error('Error matching items:', error);
      }
    };
      //  Approve match and delete the matched lost item

    fetchAndMatch();
  }, []);

  const handleApprove = async (match) => {
    try {
      await updateDoc(doc(db, 'claims', match.claimId), {
        approved: true,
        status: 'approved',
        updatedAt: new Date().toISOString()
      });

      await deleteDoc(doc(db, 'lostItems', match.matchedLostItem.id));

      setMatches((prev) =>
        prev.filter((m) => m.claimId !== match.claimId)
      );

      alert('✅ Match approved and forwarded to security section.');
    } catch (error) {
      console.error('Error approving match:', error);
      alert('Something went wrong.');
    }
  };

  const handleReject = async (match) => {
    try {
      await updateDoc(doc(db, 'claims', match.claimId), {
        status: 'rejected',
        updatedAt: new Date().toISOString()
      });

      setMatches((prev) =>
        prev.filter((m) => m.claimId !== match.claimId)
      );

      alert('❌ Match rejected.');
    } catch (error) {
      console.error('Error rejecting match:', error);
      alert('Something went wrong.');
    }
  };

  const getColor = (score) => {
    if (score === 1) return '#4CAF50';
    if (score >= 0.7) return '#FF5722';
    if (score >= 0.5) return '#FFC107';
    return '#FFEB3B';
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h2>🤖 Matched Items with AI Score</h2>
      {matches.length === 0 ? (
        <p>No matches found yet.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {matches.map((match, index) => (
            <li
              key={index}
              style={{
                marginBottom: '2rem',
                padding: '1.5rem',
                border: '1px solid #ccc',
                borderRadius: '10px',
                backgroundColor: '#fdfdfd',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              }}
            >
              <strong>Match #{index + 1}</strong>
              <p>
                <strong>Claimed:</strong> {match.claimItem.itemName} — {match.claimItem.itemDescription}
              </p>
              <p>
                <strong>Matched Lost:</strong> {match.matchedLostItem.itemName} — {match.matchedLostItem.description}
              </p>
              <p>
                <strong>Status:</strong> {match.score}
              </p>

              {/* Visual Score Chart */}
              <ResponsiveContainer width="100%" height={150}>
                <BarChart data={[{ name: 'Match Score', value: match.rawScore * 100 }]}>
                  <XAxis dataKey="name" hide />
                  <YAxis domain={[0, 100]} hide />
                  <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                  <Bar dataKey="value">
                    <Cell fill={getColor(match.rawScore)} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              {/* Action Buttons */}
              <div style={{ marginTop: '1rem' }}>
                {match.rawScore === 1 && (
                  <button
                    onClick={() => handleApprove(match)}
                    style={{
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      padding: '0.6rem 1.2rem',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      marginRight: '1rem'
                    }}
                  >
                    ✅ Approve
                  </button>
                )}

                <button
                  onClick={() => handleReject(match)}
                  style={{
                    backgroundColor: '#D32F2F',
                    color: 'white',
                    padding: '0.6rem 1.2rem',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  ❌ Reject
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ItemMatcher;
